import { Suspense, useCallback } from 'react';

import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react';

import { animations } from '../../config/animations';
import { useHiddenBodyOverflow } from '../../hooks/useHiddenBodyOverflow';
import { useStores } from '../../stores/useStores';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { Loader } from '../Loader/Loader';

import styles from './AppPortal.module.scss';

const AppPortal = observer(() => {
  const { portalStore } = useStores();

  useHiddenBodyOverflow(portalStore.isVisible);

  const { Component } = portalStore;
  const children = Component ? <Component /> : null;

  const handleAnimationComplete = useCallback(() => {
    if (portalStore.portalState === 'opening') {
      portalStore.fullyOpenPortal();
    }
  }, [portalStore]);

  const handleExitComplete = useCallback(() => {
    if (portalStore.portalState === 'closing') {
      portalStore.fullyClosePortal();
    }
  }, [portalStore]);

  return (
    <AnimatePresence initial={portalStore.isVisible} onExitComplete={handleExitComplete}>
      {portalStore.isVisible && (
        <motion.div
          animate={animations.opacity.visible}
          className={classNames(styles.portal, portalStore.options.portalClassName)}
          exit={animations.opacity.hidden}
          initial={animations.opacity.hidden}
          onAnimationComplete={handleAnimationComplete}
        >
          <div
            aria-hidden="true"
            className={classNames(
              styles.overlay,
              {
                [styles.overlayNotClickable]: portalStore.options.persistent,
              },
              portalStore.options.overlayClassName,
            )}
            onClick={() => portalStore.closePortal()}
          />
          <motion.div
            animate={animations.scale.visible}
            className={classNames(styles.content, portalStore.options.contentClassName)}
            exit={animations.scale.hidden}
            initial={animations.scale.hidden}
            role="dialog"
          >
            <Suspense fallback={<Loader fullScreen />}>
              <ErrorBoundary>{children}</ErrorBoundary>
            </Suspense>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default AppPortal;
